/**
 * The speed lines: what a player sees when a move gives them speed they could not run at (the
 * owner, 2026-09-20: "when you get a perfect vault there should be some visual cue ... that you got
 * a boost"). A PERFECT vault leaves the box about 1.6 m/s over the run cap; PERFECT rolls, slides,
 * and mantles also add speed. The PERFECT popup says the timing was right; these lines show the
 * resulting boost.
 *
 * Nothing moves the camera. The first cut pulled the view back a little as well, and the owner
 * called it on the same day: "the boost shakes the screen too much and feels like a jump. just do
 * the speed lines out the back and no camera shake". Under the player's hands, a camera that moves
 * on its own reads as the body moving, which is exactly the lie to avoid when the body really did
 * just get faster.
 *
 * So the cue is a rake of neon lines streaming off the back of the body, drawn behind the
 * silhouette and following it, so they stay with the player instead of being left at the box; each
 * one slides further back as the burst ages, which is what reads as speed. They fade inside half a
 * second, so the cue reads at a glance and never sits on top of the next move.
 *
 * Every sprite is made once and moved in place; the streak offsets come from a fixed seed, so two
 * bursts of the same move look the same and a screenshot of one is stable.
 */

import type { Container, Sprite } from "pixi.js";
import type { RenderFeel } from "./feel";
import { NEON } from "./palette";
import type { ShapeFactory } from "./shapes";
import type { ViewState } from "./view";

/** Streaks in a burst. A rake, not a cloud: enough to read as motion, few enough to stay sharp. */
const STREAKS = 14;

function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

export class BoostEffect {
  private readonly streaks: Sprite[] = [];
  /** Where each streak sits relative to the body when the burst starts: along travel, and up. */
  private readonly offAlong = new Float64Array(STREAKS);
  private readonly offUp = new Float64Array(STREAKS);
  /** Each streak's share of the burst's length and how fast it falls behind, 0..1. */
  private readonly lengthFrac = new Float64Array(STREAKS);
  private readonly driftFrac = new Float64Array(STREAKS);
  /** The burst: which way the body was going, and when it started (render ms). */
  private dir = 1;
  private startMs = -1e9;

  constructor(f: ShapeFactory, parent: Container) {
    const rnd = lcg(7331);
    for (let i = 0; i < STREAKS; i++) {
      this.offAlong[i] = 0.1 + rnd() * 0.7;
      this.offUp[i] = 0.1 + rnd() * 1.7;
      this.lengthFrac[i] = 0.55 + rnd() * 0.9;
      this.driftFrac[i] = 0.5 + rnd() * 1.0;
      const streak = f.rect(parent, 0, 0, 1, 1, i % 3 === 0 ? NEON.cyan : NEON.yellow, 0);
      streak.visible = false;
      this.streaks.push(streak);
    }
  }

  /** A move just pushed the body, travelling `dir`: stream the lines off its back from now. */
  start(dir: number, nowMs: number): void {
    this.dir = dir >= 0 ? 1 : -1;
    this.startMs = nowMs;
  }

  clear(): void {
    this.startMs = -1e9;
    for (const s of this.streaks) s.visible = false;
  }

  /** Place and fade the lines behind `view` for this frame. Nothing is drawn once it has run out. */
  draw(view: ViewState, feel: RenderFeel, nowMs: number): void {
    const life = feel.boostMs;
    const age = nowMs - this.startMs;
    if (age < 0 || age >= life) {
      for (const s of this.streaks) s.visible = false;
      return;
    }
    const f = age / life;
    // In fast, out slow: the rake is brightest the instant the push lands.
    const fade = f < 0.1 ? f / 0.1 : 1 - (f - 0.1) / 0.9;
    for (let i = 0; i < STREAKS; i++) {
      const s = this.streaks[i]!;
      const length = feel.boostLineLength * this.lengthFrac[i]!;
      // Each line starts just off the body's back and slides further behind as the burst ages.
      const behind = this.offAlong[i]! + feel.boostLineDrift * this.driftFrac[i]! * f;
      // The near end of the line, then the far end away from the direction of travel.
      const near = view.x - this.dir * behind;
      s.visible = true;
      s.position.set(this.dir > 0 ? near - length : near, view.y + this.offUp[i]!);
      s.width = length;
      s.height = feel.boostLineWidth;
      s.alpha = feel.boostAlpha * fade;
    }
  }
}
